import { Component, inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { InMemoryDataService } from '../data/in-memory-data.service';
import { TranslatePipe } from '../i18n/translate.pipe';
import { AuthService } from './auth.service';
import { UserContextService } from './user-context.service';

@Component({
  selector: 'app-session-expired-dialog',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, TranslatePipe],
  template: `
    <h2 mat-dialog-title>{{ 'sessionExpired.title' | t }}</h2>
    <mat-dialog-content>{{ 'sessionExpired.message' | t }}</mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-flat-button color="primary" (click)="dialogRef.close()">{{ 'common.ok' | t }}</button>
    </mat-dialog-actions>
  `
})
export class SessionExpiredDialogComponent {
  readonly dialogRef = inject(MatDialogRef<SessionExpiredDialogComponent>);
}

let sessionExpiredDialogOpen = false;

async function rejectExpiredSession() {
  const auth = inject(AuthService);
  const userContext = inject(UserContextService);
  const dataService = inject(InMemoryDataService);
  const router = inject(Router);
  const dialog = inject(MatDialog);

  if (!sessionExpiredDialogOpen) {
    sessionExpiredDialogOpen = true;
    try {
      await new Promise<void>((resolve) => {
        dialog
          .open(SessionExpiredDialogComponent, {
            width: '420px',
            maxWidth: 'calc(100vw - 32px)',
            disableClose: true
          })
          .afterClosed()
          .subscribe(() => resolve());
      });
    } finally {
      sessionExpiredDialogOpen = false;
    }
  }

  auth.clearSession();
  userContext.clearAuthenticatedUser();
  dataService.clearSessionData();
  return router.createUrlTree(['/login']);
}

async function ensureAuthenticated() {
  const auth = inject(AuthService);
  const dataService = inject(InMemoryDataService);
  const router = inject(Router);
  if (!auth.isAuthenticated()) {
    return rejectExpiredSession();
  }
  return (await dataService.ensureInitialData()) ? true : router.createUrlTree(['/login']);
}

export const authGuard: CanActivateFn = async () => {
  return ensureAuthenticated();
};

export const authMatchGuard: CanMatchFn = async () => {
  return ensureAuthenticated();
};

async function ensureOrganisationWorkspaceAccess() {
  const auth = inject(AuthService);
  const router = inject(Router);
  const authenticated = await ensureAuthenticated();
  if (authenticated !== true) {
    return authenticated;
  }
  const role = auth.currentUser()?.role;
  if (role === 'instructor' && !auth.isDelegatedOrganisationSession()) {
    return router.createUrlTree(['/instructor']);
  }
  if (role === 'student' && !auth.isDelegatedOrganisationSession()) {
    return router.createUrlTree(['/student']);
  }
  return true;
}

export const organisationWorkspaceGuard: CanActivateFn = async () => {
  return ensureOrganisationWorkspaceAccess();
};

export const organisationWorkspaceMatchGuard: CanMatchFn = async () => {
  return ensureOrganisationWorkspaceAccess();
};

export const instructorCalendarGuard: CanActivateFn = async () => {
  const userContext = inject(UserContextService);
  const router = inject(Router);
  const authenticated = await ensureAuthenticated();
  if (authenticated !== true) {
    return authenticated;
  }
  return userContext.canUseInstructorCalendar() ? true : router.createUrlTree(['/dashboard']);
};

async function ensureUserAppAccess(requiredRole: 'student' | 'instructor') {
  const auth = inject(AuthService);
  const dataService = inject(InMemoryDataService);
  const router = inject(Router);
  if (!auth.isAuthenticated()) {
    return rejectExpiredSession();
  }
  const userRole = auth.currentUser()?.role;
  if (userRole !== requiredRole && !auth.isRoot() && !auth.isDelegatedOrganisationSession()) {
    return router.createUrlTree(['/dashboard']);
  }
  return (await dataService.ensureInitialData()) ? true : router.createUrlTree(['/login']);
}

export const studentAppGuard: CanActivateFn = async () => {
  return ensureUserAppAccess('student');
};

export const studentAppMatchGuard: CanMatchFn = async () => {
  return ensureUserAppAccess('student');
};

export const instructorAppGuard: CanActivateFn = async () => {
  return ensureUserAppAccess('instructor');
};

export const instructorAppMatchGuard: CanMatchFn = async () => {
  return ensureUserAppAccess('instructor');
};

export const rootGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const dataService = inject(InMemoryDataService);
  const router = inject(Router);
  if (!auth.isAuthenticated()) {
    return rejectExpiredSession();
  }
  if (!auth.isRoot()) {
    return router.createUrlTree(['/dashboard']);
  }
  if (!(await dataService.ensureInitialData())) {
    return router.createUrlTree(['/login']);
  }
  return true;
};

export const rootMatchGuard: CanMatchFn = async () => {
  const auth = inject(AuthService);
  const dataService = inject(InMemoryDataService);
  const router = inject(Router);
  if (!auth.isAuthenticated()) {
    return rejectExpiredSession();
  }
  if (!auth.isRoot()) {
    return router.createUrlTree(['/dashboard']);
  }
  if (!(await dataService.ensureInitialData())) {
    return router.createUrlTree(['/login']);
  }
  return true;
};
